// @ts-nocheck
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import { getAuth } from 'firebase/auth';

declare global {
  interface Window {
    gtag: any;
  }
}

// ============================================================================
// GA4 EVENT CONSTANTS (Google-recommended snake_case naming)
// ============================================================================

// Page and Navigation Events
export const GA_EVENTS = {
  // Page Navigation
  PAGE_VIEW: 'page_view',
  
  // Authentication Events
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGN_UP: 'sign_up',
  
  // Property Events
  VIEW_PROPERTY: 'view_property',
  CREATE_PROPERTY: 'create_property',
  UPDATE_PROPERTY: 'update_property',
  DELETE_PROPERTY: 'delete_property',
  PUBLISH_PROPERTY: 'publish_property',
  VIEW_PROPERTY_LIST: 'view_item_list',
  
  // Place Events
  VIEW_PLACE: 'view_place',
  CREATE_PLACE: 'create_place',
  UPDATE_PLACE: 'update_place',
  DELETE_PLACE: 'delete_place',
  
  // Billing/Transaction Events (Ecommerce)
  VIEW_TRANSACTION_LIST: 'view_item_list',
  VIEW_TRANSACTION: 'view_item',
  VIEW_INVOICE: 'view_item',
  BEGIN_CHECKOUT: 'begin_checkout',
  COMPLETE_PURCHASE: 'purchase',
  
  // Form Events
  FORM_SUBMIT: 'form_submit',
  FORM_ERROR: 'form_error',
  
  // Subscription Events
  VIEW_SUBSCRIPTION_CARD: 'view_item',
  INITIATE_SUBSCRIPTION: 'begin_checkout',
  COMPLETE_SUBSCRIPTION: 'purchase',
  CANCEL_SUBSCRIPTION: 'remove_from_cart',
} as const;

// ============================================================================
// ECOMMERCE ITEM SCHEMA
// ============================================================================

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_category: string;
  item_brand?: string;
  item_variant?: string;
  item_list_id?: string;
  item_list_name?: string;
  price: number;
  currency: string;
  quantity?: number;
  discount?: number;
  promotion_id?: string;
  promotion_name?: string;
  index?: number;
}

// ============================================================================
// EVENT PARAMETER INTERFACES
// ============================================================================

export interface PageViewParams {
  page_title?: string;
  page_path?: string;
  referral_source?: 'direct' | 'utm' | 'admin_link' | 'organic';
}

export interface PropertyEventParams {
  property_id: string;
  parent_account_id?: string;
  category?: string;
  action?: string;
  status?: string;
  currency?: string;
}

export interface TransactionEventParams {
  transaction_id?: string;
  value: number;
  currency: string;
  property_id?: string;
  parent_account_id?: string;
  transaction_type?: string;
  payment_method?: string;
  coupon?: string;
}

export interface AuthEventParams {
  method?: 'email' | 'google' | 'apple';
  referral_source?: string;
}

export interface FormEventParams {
  form_name: string;
  form_id?: string;
  error_code?: string;
  error_message?: string;
  submission_status: 'success' | 'error' | 'validation_error';
}

// ============================================================================
// LANGUAGE ANALYTICS (Existing Implementation)
// ============================================================================

export interface LanguageAnalyticsEvent {
  action: 'language_auto_detected' | 'language_manually_selected' | 'language_mismatch' | 'regional_detection_success';
  category: 'i18n';
  label?: string;
  value?: number;
  custom_parameters?: {
    detected_language?: string;
    selected_language?: string;
    browser_language?: string;
    user_locale?: string;
    timestamp?: number;
    detection_method?: 'browser' | 'localStorage' | 'url' | 'cookie';
    is_regional_match?: boolean;
    country_code?: string;
    region?: string;
  };
}

// ============================================================================
// MAIN ANALYTICS CLASS
// ============================================================================

class AnalyticsManager {
  private isInitialized: boolean = false;
  private measurementId: string;
  private debugMode: boolean;

  constructor(measurementId: string) {
    this.measurementId = measurementId;
    this.debugMode = import.meta.env.VITE_ANALYTICS_DEBUG === 'true';
    this.initializeGA();
  }

  /**
   * Initialize Google Analytics 4 with React GA4
   */
  private initializeGA() {
    if (typeof window !== 'undefined' && !this.isInitialized && this.measurementId) {
      try {
        ReactGA.initialize(this.measurementId, {
          gtagOptions: {
            send_page_view: false, // We'll send custom events
            debug_mode: this.debugMode,
          }
        });
        this.isInitialized = true;

        // Set Sentry context for analytics
        Sentry.setContext('analytics', {
          initialized: true,
          measurementId: this.measurementId.substring(0, 3) + '***', // Obfuscate
          environment: import.meta.env.VITE_ENV,
          debug_mode: this.debugMode,
        });

        if (this.debugMode) {
          console.log('✅ Analytics Manager initialized with GA4');
        }
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
        Sentry.captureException(error, {
          tags: { module: 'analytics' }
        });
      }
    }
  }

  /**
   * Set user properties for GA4 and GTM
   */
  public setUserProperties(userId: string, properties: {
    user_type?: string;
    subscription_tier?: string;
    region?: string;
    language_preference?: string;
    parent_account_id?: string;
    [key: string]: any;
  }) {
    if (!this.isInitialized) return;

    try {
      // Set ReactGA user ID
      ReactGA.set({ userId: userId });

      // Set user properties via gtag
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', this.measurementId, {
          user_id: userId,
          ...properties
        });
        
        // Set user properties
        window.gtag('set', 'user_properties', properties);
      }

      // Set in Sentry for correlation
      Sentry.setUser({
        id: userId,
        ...properties
      });

      if (this.debugMode) {
        console.log('📊 User properties set:', { userId, ...properties });
      }
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Send a page view event
   */
  public trackPageView(params: PageViewParams = {}) {
    if (!this.isInitialized) return;

    try {
      const eventData = {
        page_title: params.page_title || document.title,
        page_path: params.page_path || window.location.pathname,
        referral_source: params.referral_source || 'direct',
      };

      ReactGA.event({
        action: GA_EVENTS.PAGE_VIEW,
        ...eventData
      });

      if (this.debugMode) {
        console.log('📄 Page view tracked:', eventData);
      }
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track authentication events
   */
  public trackAuthEvent(
    action: 'login' | 'logout' | 'sign_up',
    params?: AuthEventParams
  ) {
    if (!this.isInitialized) return;

    try {
      const eventData = {
        method: params?.method || 'email',
        referral_source: params?.referral_source || 'direct',
      };

      const eventName = action === 'login' ? GA_EVENTS.LOGIN :
                       action === 'logout' ? GA_EVENTS.LOGOUT :
                       GA_EVENTS.SIGN_UP;

      ReactGA.event({
        action: eventName,
        ...eventData
      });

      Sentry.captureMessage(`User ${action}`, 'info', {
        contexts: { auth: eventData }
      });

      if (this.debugMode) {
        console.log(`🔐 Auth event (${action}) tracked:`, eventData);
      }
    } catch (error) {
      console.error(`Failed to track ${action} event:`, error);
    }
  }

  /**
   * Track property events with parent account hierarchy
   */
  public trackPropertyEvent(
    action: 'view' | 'create' | 'update' | 'delete' | 'publish' | 'list',
    params: PropertyEventParams
  ) {
    if (!this.isInitialized) return;

    try {
      const eventName = action === 'view' ? GA_EVENTS.VIEW_PROPERTY :
                       action === 'create' ? GA_EVENTS.CREATE_PROPERTY :
                       action === 'update' ? GA_EVENTS.UPDATE_PROPERTY :
                       action === 'delete' ? GA_EVENTS.DELETE_PROPERTY :
                       action === 'publish' ? GA_EVENTS.PUBLISH_PROPERTY :
                       GA_EVENTS.VIEW_PROPERTY_LIST;

      const eventData = {
        property_id: params.property_id,
        parent_account_id: params.parent_account_id,
        category: params.category || 'property',
        action: action,
        status: params.status,
      };

      ReactGA.event({
        action: eventName,
        ...eventData
      });

      Sentry.addBreadcrumb({
        category: 'property',
        message: `Property ${action}`,
        level: 'info',
        data: eventData,
      });

      if (this.debugMode) {
        console.log(`🏠 Property event (${action}) tracked:`, eventData);
      }
    } catch (error) {
      console.error(`Failed to track property ${action} event:`, error);
    }
  }

  /**
   * Track transaction/invoice events with ecommerce data
   */
  public trackTransactionEvent(
    action: 'view_list' | 'view' | 'begin_checkout' | 'purchase',
    items: EcommerceItem[],
    params: TransactionEventParams
  ) {
    if (!this.isInitialized) return;

    try {
      const eventName = action === 'view_list' ? GA_EVENTS.VIEW_TRANSACTION_LIST :
                       action === 'view' ? GA_EVENTS.VIEW_TRANSACTION :
                       action === 'begin_checkout' ? GA_EVENTS.BEGIN_CHECKOUT :
                       GA_EVENTS.COMPLETE_PURCHASE;

      const eventData = {
        items: items,
        transaction_id: params.transaction_id,
        value: params.value,
        currency: params.currency,
        property_id: params.property_id,
        parent_account_id: params.parent_account_id,
        transaction_type: params.transaction_type,
        payment_method: params.payment_method,
        coupon: params.coupon,
      };

      ReactGA.event({
        action: eventName,
        ...eventData
      });

      Sentry.addBreadcrumb({
        category: 'transaction',
        message: `Transaction ${action}`,
        level: 'info',
        data: {
          transaction_id: params.transaction_id,
          value: params.value,
          currency: params.currency,
        },
      });

      if (this.debugMode) {
        console.log(`💰 Transaction event (${action}) tracked:`, eventData);
      }
    } catch (error) {
      console.error(`Failed to track transaction ${action} event:`, error);
    }
  }

  /**
   * Track form submission events
   */
  public trackFormEvent(params: FormEventParams) {
    if (!this.isInitialized) return;

    try {
      const eventData = {
        form_name: params.form_name,
        form_id: params.form_id,
        submission_status: params.submission_status,
        error_code: params.error_code,
        error_message: params.error_message,
      };

      ReactGA.event({
        action: params.submission_status === 'success' ? GA_EVENTS.FORM_SUBMIT : GA_EVENTS.FORM_ERROR,
        ...eventData
      });

      if (params.submission_status !== 'success') {
        Sentry.captureMessage(`Form error: ${params.form_name}`, 'warning', {
          contexts: { form: eventData }
        });
      }

      if (this.debugMode) {
        console.log(`📋 Form event tracked:`, eventData);
      }
    } catch (error) {
      console.error('Failed to track form event:', error);
    }
  }

  /**
   * Track place/location events
   */
  public trackPlaceEvent(
    action: 'view' | 'create' | 'update' | 'delete',
    placeId: string,
    placeName?: string,
    metadata?: Record<string, any>
  ) {
    if (!this.isInitialized) return;

    try {
      const eventName = action === 'view' ? GA_EVENTS.VIEW_PLACE :
                       action === 'create' ? GA_EVENTS.CREATE_PLACE :
                       action === 'update' ? GA_EVENTS.UPDATE_PLACE :
                       GA_EVENTS.DELETE_PLACE;

      const eventData = {
        place_id: placeId,
        place_name: placeName,
        action: action,
        ...metadata,
      };

      ReactGA.event({
        action: eventName,
        ...eventData
      });

      Sentry.addBreadcrumb({
        category: 'place',
        message: `Place ${action}`,
        level: 'info',
        data: eventData,
      });

      if (this.debugMode) {
        console.log(`📍 Place event (${action}) tracked:`, eventData);
      }
    } catch (error) {
      console.error(`Failed to track place ${action} event:`, error);
    }
  }

  /**
   * Track subscription/billing events
   */
  public trackSubscriptionEvent(
    action: 'view' | 'initiate' | 'complete' | 'cancel',
    items: EcommerceItem[],
    metadata?: Record<string, any>
  ) {
    if (!this.isInitialized) return;

    try {
      const eventName = action === 'view' ? GA_EVENTS.VIEW_SUBSCRIPTION_CARD :
                       action === 'initiate' ? GA_EVENTS.INITIATE_SUBSCRIPTION :
                       action === 'complete' ? GA_EVENTS.COMPLETE_SUBSCRIPTION :
                       GA_EVENTS.CANCEL_SUBSCRIPTION;

      const eventData = {
        items: items,
        action: action,
        ...metadata,
      };

      ReactGA.event({
        action: eventName,
        ...eventData
      });

      if (this.debugMode) {
        console.log(`📦 Subscription event (${action}) tracked:`, eventData);
      }
    } catch (error) {
      console.error(`Failed to track subscription ${action} event:`, error);
    }
  }

  /**
   * Generic event tracking for custom events
   */
  public trackCustomEvent(eventName: string, data: Record<string, any>) {
    if (!this.isInitialized) return;

    try {
      ReactGA.event({
        action: eventName,
        ...data
      });

      if (this.debugMode) {
        console.log(`🎯 Custom event (${eventName}) tracked:`, data);
      }
    } catch (error) {
      console.error(`Failed to track custom event ${eventName}:`, error);
    }
  }

  /**
   * Set custom user attributes for segmentation
   */
  public setCustomUserAttribute(attributeName: string, attributeValue: string | number | boolean) {
    if (!this.isInitialized) return;

    try {
      ReactGA.gtag.config({
        [attributeName]: attributeValue
      });

      if (this.debugMode) {
        console.log(`📌 Custom attribute set: ${attributeName} = ${attributeValue}`);
      }
    } catch (error) {
      console.error('Failed to set custom user attribute:', error);
    }
  }

  /**
   * Identify user from Firebase authentication
   */
  public identifyFirebaseUser() {
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        const userId = auth.currentUser.uid;
        const userEmail = auth.currentUser.email;

        this.setUserProperties(userId, {
          user_email: userEmail,
          firebase_uid: userId,
        });

        if (this.debugMode) {
          console.log(`🔑 Firebase user identified: ${userId}`);
        }
      }
    } catch (error) {
      console.error('Failed to identify Firebase user:', error);
    }
  }

  /**
   * Get current initialization status
   */
  public getInitializationStatus(): boolean {
    return this.isInitialized;
  }

  /**
   * Get debug mode status
   */
  public getDebugMode(): boolean {
    return this.debugMode;
  }
}

// ============================================================================
// LANGUAGE ANALYTICS CLASS (Keep existing implementation)
// ============================================================================

class LanguageAnalytics {
  private isInitialized: boolean = false;
  private measurementId: string;

  constructor(measurementId: string) {
    this.measurementId = measurementId;
    this.initializeGA();
  }

  private initializeGA() {
    if (typeof window !== 'undefined' && !this.isInitialized && this.measurementId) {
      ReactGA.initialize(this.measurementId, {
        gtagOptions: {
          send_page_view: false
        }
      });
      this.isInitialized = true;

      Sentry.setContext('analytics', {
        initialized: true,
        measurementId: this.measurementId.substring(0, 3) + '***',
        environment: import.meta.env.VITE_ENV
      });
    }
  }

  trackLanguageAutoDetected(detectedLanguage: string, method: string, browserLanguage?: string) {
    const event: LanguageAnalyticsEvent = {
      action: 'language_auto_detected',
      category: 'i18n',
      label: detectedLanguage,
      custom_parameters: {
        detected_language: detectedLanguage,
        browser_language: browserLanguage,
        detection_method: method as any,
        timestamp: Date.now()
      }
    };

    Sentry.setTag('language', detectedLanguage);
    this.sendEvent(event);
  }

  trackLanguageManuallySelected(
    selectedLanguage: string,
    previousLanguage: string,
    detectedLanguage?: string
  ) {
    const event: LanguageAnalyticsEvent = {
      action: 'language_manually_selected',
      category: 'i18n',
      label: selectedLanguage,
      custom_parameters: {
        selected_language: selectedLanguage,
        detected_language: detectedLanguage,
        timestamp: Date.now()
      }
    };

    Sentry.setTag('language', selectedLanguage);
    this.sendEvent(event);

    if (detectedLanguage && detectedLanguage !== selectedLanguage) {
      this.trackLanguageMismatch(detectedLanguage, selectedLanguage);
    }
  }

  trackLanguageMismatch(detectedLanguage: string, selectedLanguage: string) {
    const event: LanguageAnalyticsEvent = {
      action: 'language_mismatch',
      category: 'i18n',
      label: `${detectedLanguage}_to_${selectedLanguage}`,
      custom_parameters: {
        detected_language: detectedLanguage,
        selected_language: selectedLanguage,
        timestamp: Date.now()
      }
    };

    this.sendEvent(event);
  }

  trackRegionalDetectionSuccess(
    detectedLanguage: string,
    countryCode: string,
    region: string
  ) {
    const event: LanguageAnalyticsEvent = {
      action: 'regional_detection_success',
      category: 'i18n',
      label: `${region}_${detectedLanguage}`,
      custom_parameters: {
        detected_language: detectedLanguage,
        country_code: countryCode,
        region: region,
        is_regional_match: true,
        timestamp: Date.now()
      }
    };

    this.sendEvent(event);
  }

  private sendEvent(event: LanguageAnalyticsEvent) {
    if (!this.isInitialized) {
      if (import.meta.env.DEV) {
        console.warn('Analytics not initialized');
      }
      return;
    }

    ReactGA.event({
      category: event.category,
      action: event.action,
      label: event.label,
      value: event.value,
      ...event.custom_parameters
    });

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        custom_map: event.custom_parameters,
        ...event.custom_parameters
      });
    }

    if (import.meta.env.DEV) {
      console.log('Language Analytics Event:', event);
    }
  }

  getUserLanguagePreferences() {
    return {
      browserLanguages: navigator.languages || [navigator.language],
      primaryLanguage: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: Intl.DateTimeFormat().resolvedOptions().locale
    };
  }
}

// ============================================================================
// SINGLETON INSTANCES
// ============================================================================

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

// Main analytics manager instance
export const analyticsManager = new AnalyticsManager(measurementId);

// Language analytics instance (for backward compatibility)
export const languageAnalytics = new LanguageAnalytics(measurementId);

// Export for use in components
export default analyticsManager;